import React from 'react';
import styled from 'styled-components';
import { FaMapMarkerAlt, FaClock, FaCalendarAlt, FaGlassCheers, FaRing, FaDirections, FaChevronLeft, FaChevronRight, FaMusic } from 'react-icons/fa';
import { IoLogoApple, IoLogoGoogle } from 'react-icons/io5';
import { motion } from 'framer-motion';
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { createGlobalStyle } from 'styled-components';

const GlobalStyle = createGlobalStyle`
  body, html {
    overflow-x: hidden;
    width: 100%;
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }
`;

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
  padding: 0 16px 40px;
  box-sizing: border-box;
  overflow-x: hidden;
  position: relative; // Add this
  
  @media (max-width: 480px) {
    padding: 0 8px 30px;
    max-width: 100%; // Change to max-width instead of width
  }
`;

const PageTitle = styled.h1`
  text-align: center;
  color: #947060;
  font-family: 'Cormorant Garamond', serif;
  font-size: 2.8rem;
  letter-spacing: 5px;
  margin: 40px 0;
  position: relative;
  padding: 25px 0;
  font-weight: 400;
  text-transform: uppercase;
  
  @media (max-width: 768px) {
    font-size: 2.2rem;
    letter-spacing: 3px;
    padding: 20px 0;
    margin: 30px 0;
  }
  
  @media (max-width: 480px) {
    font-size: 1.8rem;
    letter-spacing: 2px;
    padding: 15px 0;
    margin: 20px 0;
  }
  
  &::before, &::after {
    content: "";
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    height: 1px;
    background: linear-gradient(to right, transparent, #d4b59e, transparent);
    width: 80%;
    max-width: 280px;
    
    @media (max-width: 480px) {
      width: 90%;
    }
  }

`;

const NameSpan = styled.span`
  display: block;
  font-family: 'Great Vibes', cursive;
  font-size: 2.4rem;
  letter-spacing: 1px;
  font-style: normal;
  text-transform: none;
  margin-top: 15px;
  color: #b38b6d;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.8rem;
    margin-top: 10px;
  }
`;

const DetailSection = styled(motion.section)`
  margin: 30px 0;
  text-align: center;
  width: 100%;
  
  @media (max-width: 480px) {
    margin: 20px 0;
    width: 98%;
  }
`;

const DetailCard = styled(motion.div)`
  background-color: #fcfaf7;
  padding: 25px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  margin-top: 20px;
  position: relative;
  overflow: hidden;
  width: 100%;
  box-sizing: border-box;
  max-width: 100%;
  
  // Disable hover animation on mobile
  @media (max-width: 768px) {
    &:hover {
      transform: none !important;
    }
  }
  
  @media (max-width: 480px) {
    padding: 15px 10px;
    margin-top: 12px;
    width: 100%;
    border-radius: 6px;
  }
`;
const SectionTitle = styled.h2`
  font-family: 'Cormorant Garamond', serif;
  color: #947060;
  font-size: 1.8rem;
  margin-bottom: 20px;
  font-weight: 500;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
    margin-bottom: 15px;
  }
  
  @media (max-width: 480px) {
    font-size: 1.4rem;
    margin-bottom: 12px;
  }
`;

const DetailItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 15px 0;
  flex-wrap: wrap;
  
  svg {
    color: #b38b6d;
    margin-right: 10px;
    font-size: 1.2rem;
    flex-shrink: 0;
  }
  
  @media (max-width: 768px) {
    justify-content: center;
    padding: 0 10px;
    margin: 12px 0;
  }
  
  @media (max-width: 480px) {
    padding: 0 5px;
    margin: 10px 0;
    
    svg {
      font-size: 1.1rem;
      margin-right: 8px;
    }
  }
`;

const DetailText = styled.p`
  font-family: 'Lato', sans-serif;
  color: #555;
  font-size: 1.1rem;
  line-height: 1.5;
  text-align: left;
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
  
  @media (max-width: 480px) {
    font-size: 0.95rem;
    line-height: 1.4;
  }
`;


const MapContainer = styled.div`
  width: 100%;
  height: 300px;
  margin: 20px 0;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  
  @media (max-width: 768px) {
    height: 250px;
    margin: 18px 0;
  }
  
  @media (max-width: 480px) {
    height: 200px;
    margin: 15px 0;
    border-radius: 6px;
  }
`;

const DirectionsContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 15px;
  flex-wrap: wrap;
  gap: 15px;
  width: 100%; // Add width definition
  
  @media (max-width: 768px) {
    gap: 12px;
  }
  
  @media (max-width: 480px) {
    flex-direction: column;
    align-items: center;
    gap: 10px;
    margin-top: 12px;
    width: 100%; // Ensure full width on mobile
  }
`;


const DirectionButton = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 15px;
  border-radius: 50px;
  background-color: ${props => props.color || '#947060'};
  color: white;
  text-decoration: none;
  font-family: 'Lato', sans-serif;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  white-space: nowrap; // Prevent text wrapping within button
  
  @media (max-width: 480px) {
    width: 100%; // Take full container width
    max-width: 300px; // But not too wide
    padding: 10px 15px;
    font-size: 0.9rem;
    white-space: normal; // Allow text to wrap on very small screens
  }
  
  svg {
    margin-right: 8px;
    flex-shrink: 0; // Prevent icon from shrinking
  }
`;

const Separator = styled.div`
  width: 40%;
  height: 1px;
  background: linear-gradient(to right, transparent, #d4b59e, transparent);
  margin: 30px auto;
  
  @media (max-width: 480px) {
    width: 60%;
    margin: 22px auto;
  }
`;

const CountdownContainer = styled(motion.div)`
  display: flex;
  justify-content: center;
  margin: 30px 0;
  width: 100%;
  flex-wrap: wrap;
  box-sizing: border-box;
  
  @media (max-width: 480px) {
    margin: 20px 0;
  }
`;

const CountdownItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 0 15px;
  
  @media (max-width: 768px) {
    margin: 0 10px;
  }
  
  @media (max-width: 480px) {
    margin: 0 8px;
  }
`;

const CountdownNumber = styled.div`
  font-family: 'Cormorant Garamond', serif;
  font-size: 2.5rem;
  color: #947060;
  font-weight: 500;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.8rem;
  }
`;

const CountdownLabel = styled.div`
  font-family: 'Lato', sans-serif;
  font-size: 0.9rem;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 1px;
`;


const CarouselTitle = styled.h3`
  font-family: 'Cormorant Garamond', serif;
  color: #947060;
  font-size: 1.4rem;
  margin-bottom: 15px;
  font-weight: 500;
  text-align: center;
`;

const StyledSlider = styled(Slider)`
  width: 100%;
  max-width: 100%; // Add this
  
  .slick-slide {
    padding: 0;
    box-sizing: border-box;
  }
  
  .slick-track {
    display: flex; // Add this
  }
  
  .slick-list {
    overflow: hidden;
    max-width: 100%; // Add this
    padding: 0 !important; // Add this to override any inline padding
  }
  
  .slick-prev, .slick-next {
    z-index: 10;
    width: 40px;
    height: 40px;
    
    &:before {
      display: none;
    }
  }
  
  .slick-prev {
    left: 10px;
    
    @media (max-width: 768px) {
      left: 5px;
    }
  }
  
  .slick-next {
    right: 10px;
    
    @media (max-width: 768px) {
      right: 5px;
    }
  }
  
  .slick-dots {
    bottom: -30px;
    
    li button:before {
      color: #947060;
      opacity: 0.5;
    }
    
    li.slick-active button:before {
      color: #947060;
      opacity: 1;
    }
  }

  // Reduced animation complexity on mobile
  @media (max-width: 768px) {
    .slick-slide {
      transform: none !important;
      transition: opacity 0.5s ease !important;
    }
  }
`;

const CarouselContainer = styled.div`
  width: 100%;
  margin: 15px 0 30px;
  position: relative;
  padding: 0 0 40px;
  box-sizing: border-box;
  overflow: hidden;
  max-width: 100%;
  
  @media (max-width: 480px) {
    margin: 10px 0 25px;
    padding: 0 0 35px;
  }
`;



const ArrowButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background-color: rgba(255, 255, 255, 0.9);
  border-radius: 50%;
  cursor: pointer;
  color: #5a4a42;
  box-shadow: 0 4px 12px rgba(179, 139, 109, 0.2);
  transition: all 0.3s ease;
  position: absolute;
  top: 50%;
  z-index: 20;
  transform: translateY(-50%);
  ${props => props.className?.includes('slick-prev') ? 'left: 10px;' : ''}
  ${props => props.className?.includes('slick-next') ? 'right: 10px;' : ''}
  border: 1px solid rgba(179, 139, 109, 0.2);
  
  @media (max-width: 768px) {
    ${props => props.className?.includes('slick-prev') ? 'left: 5px;' : ''}
    ${props => props.className?.includes('slick-next') ? 'right: 5px;' : ''}
    width: 36px;
    height: 36px;
  }
  
  @media (max-width: 480px) {
    width: 32px;
    height: 32px;
  }
  
  &:hover {
    transform: translateY(-50%) translateY(-3px);
    box-shadow: 0 6px 16px rgba(179, 139, 109, 0.3);
    background-color: white;
  }
  
  &:active {
    transform: translateY(-50%);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  }
  
  svg {
    font-size: 1.2rem;
    color: #5a4a42;
    display: block;
    margin: 0 auto;
    line-height: 1;
    padding: 0;
    
    @media (max-width: 480px) {
      font-size: 1rem;
    }
  }
`;

const ImageContainer = styled.div`
  position: relative;
  width: 100%;
  height: 350px;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    height: 250px;
  }
  
  @media (max-width: 576px) {
    height: 200px;
    border-radius: 6px;
  }
  
  @media (max-width: 480px) {
    height: 180px;
    width: 100%; // Ensure full width
  }
`;
const MobileHidden = styled.div`
  @media (max-width: 768px) {
    .desktop-carousel {
      display: none;
    }
  }
  @media (min-width: 769px) {
    .mobile-gallery {
      display: none;
    }
  }
`;

const MobileVenueImage = styled.img`
  width: 100%;
  max-width: 100%;
  height: auto;
  border-radius: 8px;
  margin: 10px 0;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
`;


const VenueImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  
  // Optimize for mobile - reduce quality slightly for performance
  @media (max-width: 768px) {
    image-rendering: auto;
  }
`;

const DJImage = styled.img`
  width: 120px;
  height: 120px;
  border-radius: 50%;
  object-fit: cover;
  margin: 15px auto;
  border: 3px solid #d4b59e;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  
  @media (max-width: 480px) {
    width: 100px;
    height: 100px;
    margin: 12px auto;
    border: 2px solid #d4b59e;
  }
`;

const DJContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 10px 0 20px;
  width: 100%; // Add width
  box-sizing: border-box;
  
  @media (max-width: 480px) {
    margin: 5px 0 15px;
  }
`;

const DJName = styled.h3`
  font-family: 'Lato', sans-serif;
  color: #947060;
  font-size: 1.3rem;
  font-weight: 600;
  margin: 10px 0 5px;
  
  @media (max-width: 480px) {
    font-size: 1.2rem;
    margin: 8px 0 3px;
  }
`;

const DJDescription = styled.p`
  font-family: 'Lato', sans-serif;
  color: #666;
  font-size: 1rem;
  line-height: 1.5;
  text-align: center;
  max-width: 95%; // Adjust max-width to avoid overflow
  box-sizing: border-box;
  overflow-wrap: break-word; // Ensure text wrapping
  
  @media (max-width: 480px) {
    font-size: 0.95rem;
    line-height: 1.4;
    max-width: 100%; // Take full width on mobile
    padding: 0 5px; // Add some padding
  }
`;

const SongRequestLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-top: 15px;
  padding: 10px 20px;
  border-radius: 50px;
  background-color: #947060;
  color: white;
  text-decoration: none;
  font-family: 'Lato', sans-serif;
  font-size: 0.95rem;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    background-color: #85655c;
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  }
  
  svg {
    margin-right: 8px;
    font-size: 1.1rem;
  }
  
  @media (max-width: 480px) {
    padding: 8px 18px;
    font-size: 0.9rem;
    margin-top: 12px;
    
    svg {
      font-size: 1rem;
    }
  }
`;


// Custom arrow components for the carousel
const PrevArrow = (props) => (
  <ArrowButton {...props} className={`slick-arrow slick-prev ${props.className || ''}`}>
    <FaChevronLeft />
  </ArrowButton>
);

const NextArrow = (props) => (
  <ArrowButton {...props} className={`slick-arrow slick-next ${props.className || ''}`}>
    <FaChevronRight />
  </ArrowButton>
);

const WeddingDetails = () => {
  const [countdown, setCountdown] = React.useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });
  
  // Wedding date - October 25, 2025 at 4:00 PM
  const weddingDate = new Date('October 25, 2025 16:00:00').getTime();
  
  React.useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date().getTime();
      const distance = weddingDate - now;
      
      if (distance < 0) {
        return { days: 0, hours: 0, minutes: 0, seconds: 0 };
      }
      
      const days = Math.floor(distance / (1000 * 60 * 60 * 24));
      const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((distance % (1000 * 60)) / 1000);
      
      return { days, hours, minutes, seconds };
    };

    setCountdown(calculateTimeLeft());
    
    // Update less frequently on mobile
    const isMobile = window.innerWidth < 768;
    const interval = isMobile ? 10000 : 1000; // 10 seconds on mobile, 1 second on desktop
    
    const timer = setInterval(() => {
      setCountdown(calculateTimeLeft());
    }, interval);
    
    return () => clearInterval(timer);
  }, [weddingDate]);
  
  // Google Maps embed URL for White House Banquet and Event Center
  const mapSrc = "https://www.google.com/maps/embed/v1/place?q=place_id:ChIJySrEaBYp3YAR05R1HE2kgUc&key=AIzaSyA0WbStXmsy2ggJ157SvdV0_9k2GqtIe3g";
  
  // Google Maps and Apple Maps direction links
  const googleMapsLink = "https://www.google.com/maps/dir/?api=1&destination=White+House+Banquet+and+Event+Center,+1238+S+Beach+Blvd,+Anaheim,+CA";
  const appleMapsLink = "http://maps.apple.com/?address=1238+S+Beach+Blvd,+Anaheim,+CA&daddr=1238+Beach+Blvd,+Anaheim,+CA&dirflg=d";
  const [mapLoaded, setMapLoaded] = React.useState(false);
  
  const mapRef = React.useRef(null);
  
  React.useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setMapLoaded(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    
    if (mapRef.current) {
      observer.observe(mapRef.current);
    }
    
    return () => {
      if (mapRef.current) {
        observer.disconnect();
      }
    };
  }, []);
  // Venue photos
  const venuePhotos = [
    {
      id: 1,
      src: "/img/whitehouse_1.jpg",
      alt: "White House Banquet Hall - View 1"
    },
    {
      id: 2,
      src: "/img/whitehouse_2.jpg",
      alt: "White House Banquet Hall - View 2"
    },
    {
      id: 3,
      src: "/img/whitehouse_3.jpg",
      alt: "White House Banquet Hall - View 3"
    },
    {
      id: 4,
      src: "/img/whitehouse_4.jpg",
      alt: "White House Banquet Hall - View 4"
    },
    {
      id: 5,
      src: "/img/whitehouse_5.jpg",
      alt: "White House Banquet Hall - View 5"
    },
    {
      id: 6,
      src: "/img/whitehouse_6.jpg",
      alt: "White House Banquet Hall - View 6"
    },
    {
      id: 7,
      src: "/img/whitehouse_7.jpg",
      alt: "White House Banquet Hall - View 7"
    }
  ];

  // const songRequestLink = "https://forms.gle/1234567890";
  // Carousel settings
  const settings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 4000,
    pauseOnHover: true,
    pauseOnFocus: true,
    pauseOnDotsHover: true,
    swipeToSlide: true,
    touchThreshold: 10,
    lazyLoad: 'ondemand', // Add lazy loading
    prevArrow: <PrevArrow />,
    nextArrow: <NextArrow />,
    // On mobile, disable autoplay and simplify
    responsive: [
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 1,
          autoplay: false,
          dots: false // Simpler UI on mobile
        }
      }
    ]
  };

  return (
    <PageContainer>
      <PageTitle>
        Wedding Details
        <NameSpan>Erika & Andrew</NameSpan>
      </PageTitle>
      
      <CountdownContainer
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <CountdownItem>
          <CountdownNumber>{countdown.days}</CountdownNumber>
          <CountdownLabel>Days</CountdownLabel>
        </CountdownItem>
        <CountdownItem>
          <CountdownNumber>{countdown.hours}</CountdownNumber>
          <CountdownLabel>Hours</CountdownLabel>
        </CountdownItem>
        <CountdownItem>
          <CountdownNumber>{countdown.minutes}</CountdownNumber>
          <CountdownLabel>Minutes</CountdownLabel>
        </CountdownItem>
        <CountdownItem>
          <CountdownNumber>{countdown.seconds}</CountdownNumber>
          <CountdownLabel>Seconds</CountdownLabel>
        </CountdownItem>
      </CountdownContainer>
      
      <DetailSection
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <DetailCard
          whileHover={{ translateY: -5 }}
          transition={{ type: "spring", stiffness: 400, damping: 10 }}
        >
          <SectionTitle>When & Where</SectionTitle>
          <DetailItem>
            <FaCalendarAlt />
            <DetailText>Saturday, October 25, 2025</DetailText>
          </DetailItem>
          <DetailItem>
            <FaClock />
            <DetailText>Arrive by 5:00 PM</DetailText>
          </DetailItem>
          <DetailItem>
            <FaMapMarkerAlt />
            <DetailText>White House Banquet and Event Center<br />1238 S. Beach Blvd, Anaheim, CA</DetailText>
          </DetailItem>
          
          
          <MapContainer>
            <iframe 
              src={mapSrc}
              width="100%" 
              height="100%" 
              style={{ border: 0 }} 
              allowFullScreen="" 
              loading="lazy" 
              referrerPolicy="no-referrer-when-downgrade"
              title="Wedding Venue Map"
            ></iframe>
          </MapContainer>
          
          <DirectionsContainer>
            <DirectionButton 
              href={googleMapsLink} 
              target="_blank" 
              rel="noopener noreferrer"
              color="#4285F4"
            >
              <IoLogoGoogle /> Get Directions with Google Maps
            </DirectionButton>
            <DirectionButton 
              href={appleMapsLink} 
              target="_blank" 
              rel="noopener noreferrer"
              color="#555555"
            >
              <IoLogoApple /> Get Directions with Apple Maps
            </DirectionButton>
          </DirectionsContainer>
        </DetailCard>
      </DetailSection>
      
      <Separator />

      <DetailSection
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
        <DetailCard
          whileHover={{ translateY: -5 }}
          transition={{ type: "spring", stiffness: 400, damping: 10 }}
        >
          <MobileHidden>
            {/* Desktop version - full carousel */}
            <CarouselContainer
              className="desktop-carousel"
              as={motion.div}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <CarouselTitle>Venue Photos</CarouselTitle>
              <StyledSlider {...settings}>
                {venuePhotos.map(photo => (
                  <div key={photo.id}>
                    <ImageContainer>
                      <VenueImage src={photo.src} alt={photo.alt} />
                    </ImageContainer>
                  </div>
                ))}
              </StyledSlider>
            </CarouselContainer>
            
            {/* Mobile version - simple static images */}
            <div className="mobile-gallery">
              <CarouselTitle>Venue Photos</CarouselTitle>
              {/* Only show the first 3 images on mobile */}
              <MobileVenueImage 
                src="/img/whitehouse_1.jpg" 
                alt="White House Banquet Hall - Main View" 
                loading="lazy"
              />
              <DetailText style={{fontSize: '0.9rem', marginBottom: '15px', textAlign: 'center'}}>
                Our beautiful venue - White House Banquet and Event Center
              </DetailText>
            </div>
          </MobileHidden>
        </DetailCard>
      </DetailSection>

      <Separator />

      <DetailSection
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.7 }}
      >
        <DetailCard
          whileHover={{ translateY: -5 }}
          transition={{ type: "spring", stiffness: 400, damping: 10 }}
        >
          <SectionTitle>Our MC/DJ</SectionTitle>
          
          <DJContainer>
            <DJImage src="/img/dj-photo.jpg" alt="Paul PK Kim" />
            <DJName>Paul PK Kim</DJName>
            <DJDescription>
              We're thrilled to have PK bringing energy and entertainment to our special day! PK is a regular performer at The World Famous Laugh Factory in Hollywood, Long Beach, and Las Vegas, as well as The Comedy Store, Improv, Ice House and The Haha.
              <br /><br />
              He is the Founder, Executive Director, and MC for ten years of the non-profit organization Kollaboration, an annual talent show now in its 17th year that draws over 7,000 people in 12 cities. He has emceed over 80 Kollaboration shows across the country.
              <br /><br />
              PK was even invited to The White House as a Champion of Change for his contributions to the community!
            </DJDescription>
          </DJContainer>
          
          <DetailItem>
            <FaMusic />
            <DetailText>Professional MC and DJ services for our ceremony and reception</DetailText>
          </DetailItem>
          
          <DirectionsContainer style={{ marginTop: '20px' }}>
            <DirectionButton 
              href="https://www.pkcomedy.com" 
              target="_blank" 
              rel="noopener noreferrer"
              color="#947060"
            >
              <FaMusic /> Visit PK's Comedy Website
            </DirectionButton>
            {/* <DirectionButton 
              href="https://www.kollaboration.org" 
              target="_blank" 
              rel="noopener noreferrer"
              color="#555555"
            >
              <FaMusic /> Learn About Kollaboration
            </DirectionButton> */}
          </DirectionsContainer>
        </DetailCard>
      </DetailSection>

      <Separator />
      
      <DetailSection
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.4 }}
      >
        <DetailCard
          whileHover={{ translateY: -5 }}
          transition={{ type: "spring", stiffness: 400, damping: 10 }}
        >
          <SectionTitle>Schedule of Events</SectionTitle>
          <DetailItem>
            <FaRing />
            <DetailText>5:30 PM - Wedding Ceremony</DetailText>
          </DetailItem>
          <DetailItem>
            <FaGlassCheers />
            <DetailText>6:00 PM - Cocktail Hour</DetailText>
          </DetailItem>
          <DetailItem>
            <FaGlassCheers />
            <DetailText>7:00 PM - Reception & Dinner</DetailText>
          </DetailItem>
          <DetailItem>
            <FaGlassCheers />
            <DetailText>12:00 AM - End</DetailText>
          </DetailItem>
        </DetailCard>
      </DetailSection>
      
      <Separator />
      
      <DetailSection
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.6 }}
      >
        <DetailCard
          whileHover={{ translateY: -5 }}
          transition={{ type: "spring", stiffness: 400, damping: 10 }}
        >
          <SectionTitle>Attire</SectionTitle>
          <DetailText>
            <h2>What to Wear: Semi-Formal ✨</h2>
            <p>We’d love for everyone to feel polished but comfortable.</p>
            <ul>
              <li>
                <strong>For the guys:</strong> A suit and tie, or nice slacks with a button-down 
                and blazer. Dress shoes work best.
              </li>
              <li>
                <strong>For the girls:</strong> A cocktail or midi dress, a classy jumpsuit, or 
                a dressy skirt-and-top combo. Pair with heels, wedges, or elegant flats—whatever 
                feels good for dancing!
              </li>
              <li>
                <strong>Overall vibe:</strong> Think dressed up, but not black-tie fancy.
              </li>
            </ul>
          </DetailText>
        </DetailCard>
      </DetailSection>
    </PageContainer>
  );
};

export default WeddingDetails;